import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { Line2 } from "three/addons/lines/Line2.js";
import { LineGeometry } from "three/addons/lines/LineGeometry.js";
import { LineMaterial } from "three/addons/lines/LineMaterial.js";
import { wallPieces, type Layout, type Placed, type Point, type Rect } from "./geometry";

/**
 * Omborning 3D sahnasi (three.js).
 *
 * Ko'rinish — «taktik» monoxrom: kulrang hajmlar (palletdagi yuk),
 * ingichka oq chiziqlar (stellaj ramasi), oq kvadrat belgilar va mono
 * yozuvlar. Rang FAQAT ma'no uchun: sariq — marshrut, kara va
 * nazoratdagi pallet; qizil — bloklangan katak.
 *
 * Unumdorlik: ~15 ming katak ikkita `InstancedMesh` da (band va bo'sh),
 * stellaj ramasi bitta `LineSegments` da; tanlash — raycast, yozuvlar
 * — HTML qatlam va masofaga qarab (LOD) yashiriladi. Sahna faqat
 * o'zgarganda qayta chiziladi.
 */

/** mm → sahna birligi (metr). */
const S = 1 / 1000;

export type CellVisual = "full" | "partial" | "empty" | "blocked" | "inactive";

export interface SceneCellItem {
  locationId: string;
  code: string;
  placed: Placed;
  visual: CellVisual;
}

export interface PalletMarker {
  key: string;
  placed: Placed;
  /** Bir katakdagi tartib raqami (yonma-yon siljitiladi). */
  slot: number;
  tone: "control" | "shipping" | "stored";
  label: string;
}

export interface WorkerMarker {
  id: string;
  kind: "forklift" | "person";
  point: Point;
  /** Qaragan tomon (radian, Y o'qi atrofida). */
  heading: number;
  stale: boolean;
  label: string;
  sub: string;
  status: "live" | "idle" | "stale";
}

export interface RouteOverlay {
  points: Point[];
  stops: Array<{ point: Point; label: string }>;
  /** Marshrut «xodim → katak» (to'liq) yoki «Nazorat → katak» (punktir). */
  dashed?: boolean;
}

export type Selection =
  | { type: "cell"; locationId: string }
  | { type: "pallet"; key: string }
  | { type: "worker"; id: string };

export interface SceneHandle {
  zoomIn(): void;
  zoomOut(): void;
  reset(): void;
  focusRect(rect: Rect): void;
  focusPoint(point: Point): void;
  /** Nuqtalar (marshrut) to'liq ko'rinadigan, tepadanroq ko'rinish. */
  focusPoints(points: Point[]): void;
  /**
   * FPV (birinchi shaxs): `spawn` nuqtada, `yaw` tomonga qarab (0 —
   * shimolga, -Z). Oldingi orbit ko'rinishi saqlanadi va chiqishda qaytadi.
   */
  enterFpv(spawn: Point, yaw: number): void;
  exitFpv(): void;
}

interface Props {
  layout: Layout;
  cells: SceneCellItem[];
  pallets: PalletMarker[];
  workers: WorkerMarker[];
  route: RouteOverlay | null;
  selection: Selection | null;
  onSelect: (selection: Selection | null) => void;
  /** Marshrut ko'rinishi: stellajdagi yuk shaffof — yo'l va odam ko'rinsin. */
  dim?: boolean;
  /** FPV holati o'zgardi (Esc bilan chiqish ham shu yerdan bildiriladi). */
  onFpvChange?: (state: FpvStatus) => void;
}

export interface FpvStatus {
  active: boolean;
  /** Sichqoncha ushlangan (Pointer Lock). */
  locked: boolean;
  /** Ko'z balandligi: odam yoki kara haydovchisi. */
  forklift: boolean;
}

/** FPV: ko'z balandligi (m), tezlik (m/s), tana radiusi (mm). */
const FPV = {
  eyePerson: 1.7,
  eyeForklift: 2.5,
  walk: 3.2,
  run: 8,
  climb: 1.6,
  radius: 250,
  look: 0.0022,
};

interface FpvState {
  active: boolean;
  yaw: number;
  pitch: number;
  eye: number;
  forklift: boolean;
  x: number;
  z: number;
  vx: number;
  vz: number;
  keys: Set<string>;
  locked: boolean;
  dragging: boolean;
  saved: { position: THREE.Vector3; target: THREE.Vector3; near: number } | null;
  last: number;
  hoverAt: number;
}

const COLORS = {
  background: 0x0b0c0e,
  floor: 0x121417,
  grid: 0x1a1d21,
  outline: 0xd8dce2,
  frame: 0x9aa1ab,
  full: 0xc6cbd3,
  partial: 0x8a9099,
  empty: 0x7d848d,
  blocked: 0xd0453b,
  inactive: 0x2a2d32,
  accent: 0xe8c547,
  white: 0xf3f4f6,
  palletBase: 0x4a4038,
  shipping: 0xeef0f3,
  stored: 0x9aa0a8,
};

const ZONE_TONE: Record<string, number> = {
  control: 0x9d8be0,
  return: 0x6f9be0,
  receipt: 0xd9725f,
  customs: 0xa3a9b1,
  buffer: 0x8a9099,
  defect: 0xc0605a,
  building: 0x5c636c,
};

const VISUAL_COLOR: Record<CellVisual, number> = {
  full: COLORS.full,
  partial: COLORS.partial,
  empty: COLORS.empty,
  blocked: COLORS.blocked,
  inactive: COLORS.inactive,
};

interface LabelEntry {
  el: HTMLDivElement;
  world: THREE.Vector3;
  /** Kamera shundan uzoq bo'lsa yashiriladi (LOD). */
  maxDistance: number;
}

interface Ctx {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  staticGroup: THREE.Group;
  cellGroup: THREE.Group;
  palletGroup: THREE.Group;
  workerGroup: THREE.Group;
  routeGroup: THREE.Group;
  selectGroup: THREE.Group;
  labels: Map<string, LabelEntry[]>;
  pickables: THREE.Object3D[];
  solidIndex: SceneCellItem[];
  solidMaterial: THREE.MeshStandardMaterial | null;
  ghostIndex: SceneCellItem[];
  palletIndex: PalletMarker[];
  dirty: boolean;
  fly: { from: THREE.Vector3; to: THREE.Vector3; fromTarget: THREE.Vector3; toTarget: THREE.Vector3; start: number } | null;
  lineMaterials: LineMaterial[];
  home: { position: THREE.Vector3; target: THREE.Vector3 };
  fpv: FpvState;
  enterFpv: (spawn: Point, yaw: number) => void;
  exitFpv: () => void;
}

export const Scene3D = forwardRef<SceneHandle, Props>(function Scene3D(
  { layout, cells, pallets, workers, route, selection, onSelect, dim = false, onFpvChange },
  ref,
) {
  const host = useRef<HTMLDivElement>(null);
  const labelHost = useRef<HTMLDivElement>(null);
  const tooltip = useRef<HTMLDivElement>(null);
  const ctxRef = useRef<Ctx | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const onFpvRef = useRef(onFpvChange);
  onFpvRef.current = onFpvChange;
  // To'qnashuv uchun joriy joylashuv (sahna bir marta yaratiladi).
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const dimRef = useRef(dim);
  dimRef.current = dim;

  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx?.solidMaterial) return;
    applyDim(ctx.solidMaterial, dim);
    ctx.dirty = true;
  }, [dim]);

  // --- Sahna, kamera, boshqaruv: bir marta ---
  useEffect(() => {
    const element = host.current;
    if (!element) return;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: true });
    } catch (cause) {
      setFailed(cause instanceof Error ? cause.message : String(cause));
      return;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(COLORS.background);
    element.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(COLORS.background, 180, 420);
    const camera = new THREE.PerspectiveCamera(42, 1, 0.3, 900);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.12;
    controls.maxPolarAngle = Math.PI * 0.47;
    controls.minDistance = 4;
    controls.maxDistance = 320;
    controls.screenSpacePanning = false;

    scene.add(new THREE.HemisphereLight(0xffffff, 0x30343a, 1.25));
    const sun = new THREE.DirectionalLight(0xffffff, 1.35);
    sun.position.set(-60, 120, 80);
    scene.add(sun);
    const rim = new THREE.DirectionalLight(0xbfc6d0, 0.45);
    rim.position.set(90, 40, -60);
    scene.add(rim);

    const group = () => {
      const g = new THREE.Group();
      scene.add(g);
      return g;
    };
    const ctx: Ctx = {
      renderer, scene, camera, controls,
      staticGroup: group(), cellGroup: group(), palletGroup: group(), workerGroup: group(),
      routeGroup: group(), selectGroup: group(),
      labels: new Map(), pickables: [], solidIndex: [], solidMaterial: null, ghostIndex: [], palletIndex: [],
      dirty: true, fly: null, lineMaterials: [],
      home: { position: new THREE.Vector3(), target: new THREE.Vector3() },
      fpv: {
        active: false, yaw: 0, pitch: 0, eye: FPV.eyePerson, forklift: false, x: 0, z: 0, vx: 0, vz: 0,
        keys: new Set(), locked: false, dragging: false, saved: null, last: 0, hoverAt: 0,
      },
      enterFpv: () => {},
      exitFpv: () => {},
    };
    ctxRef.current = ctx;

    const resize = () => {
      const width = element.clientWidth || 1;
      const height = element.clientHeight || 1;
      renderer.setSize(width, height, false);
      renderer.domElement.style.width = "100%";
      renderer.domElement.style.height = "100%";
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      for (const material of ctx.lineMaterials) material.resolution.set(width, height);
      ctx.dirty = true;
    };
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    resize();

    controls.addEventListener("change", () => {
      ctx.dirty = true;
    });

    // --- Tanlash: bosish (sudrash emas) ---
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let down: { x: number; y: number } | null = null;
    const hit = (event: PointerEvent | null) => {
      if (event) {
        const rect = renderer.domElement.getBoundingClientRect();
        pointer.set(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1);
      } else {
        // FPV: nishon — ekran markazi.
        pointer.set(0, 0);
      }
      raycaster.setFromCamera(pointer, camera);
      const found = raycaster.intersectObjects(ctx.pickables, true)[0];
      if (!found) return null;
      let object: THREE.Object3D | null = found.object;
      while (object && object.userData.workerId === undefined && object.userData.pick === undefined) object = object.parent;
      if (!object) return null;
      if (object.userData.workerId !== undefined) {
        return { selection: { type: "worker", id: object.userData.workerId as string } as Selection, text: object.userData.label as string };
      }
      const index = found.instanceId ?? -1;
      if (object.userData.pick === "solid" && ctx.solidIndex[index]) {
        const item = ctx.solidIndex[index]!;
        return { selection: { type: "cell", locationId: item.locationId } as Selection, text: item.code };
      }
      if (object.userData.pick === "ghost" && ctx.ghostIndex[index]) {
        const item = ctx.ghostIndex[index]!;
        return { selection: { type: "cell", locationId: item.locationId } as Selection, text: item.code };
      }
      if (object.userData.pick === "pallet" && ctx.palletIndex[index]) {
        const item = ctx.palletIndex[index]!;
        return { selection: { type: "pallet", key: item.key } as Selection, text: item.label };
      }
      return null;
    };
    // --- FPV: yurish, qarash, to'qnashuv ---
    const fpv = ctx.fpv;
    const notifyFpv = () => onFpvRef.current?.({ active: fpv.active, locked: fpv.locked, forklift: fpv.forklift });
    const free = (x: number, z: number) => {
      const current = layoutRef.current;
      const r = FPV.radius;
      return current.isFree({ x, z }) && current.isFree({ x: x + r, z }) && current.isFree({ x: x - r, z }) &&
        current.isFree({ x, z: z + r }) && current.isFree({ x, z: z - r });
    };
    const placeCamera = () => {
      camera.position.set(fpv.x * S, fpv.eye, fpv.z * S);
      camera.rotation.set(fpv.pitch, fpv.yaw, 0, "YXZ");
      ctx.dirty = true;
    };
    ctx.enterFpv = (spawn, yaw) => {
      const start = layoutRef.current.nearestFree(spawn) ?? layoutRef.current.nearestFree(layoutRef.current.depot());
      if (!start) return;
      if (!fpv.active) {
        fpv.saved = { position: camera.position.clone(), target: controls.target.clone(), near: camera.near };
      }
      ctx.fly = null;
      fpv.active = true;
      fpv.x = start.x;
      fpv.z = start.z;
      fpv.vx = 0;
      fpv.vz = 0;
      fpv.yaw = yaw;
      fpv.pitch = -0.08;
      fpv.eye = fpv.forklift ? FPV.eyeForklift : FPV.eyePerson;
      fpv.last = performance.now();
      controls.enabled = false;
      camera.near = 0.05;
      camera.updateProjectionMatrix();
      placeCamera();
      notifyFpv();
    };
    ctx.exitFpv = () => {
      if (!fpv.active) return;
      fpv.active = false;
      fpv.keys.clear();
      fpv.dragging = false;
      if (document.pointerLockElement === renderer.domElement) document.exitPointerLock();
      fpv.locked = false;
      if (fpv.saved) {
        camera.position.copy(fpv.saved.position);
        controls.target.copy(fpv.saved.target);
        camera.near = fpv.saved.near;
        camera.updateProjectionMatrix();
      }
      fpv.saved = null;
      controls.enabled = true;
      camera.lookAt(controls.target);
      const tip = tooltip.current;
      if (tip) tip.style.display = "none";
      ctx.dirty = true;
      notifyFpv();
    };
    const selectAtCrosshair = () => onSelectRef.current(hit(null)?.selection ?? null);
    const look = (dx: number, dy: number) => {
      fpv.yaw -= dx * FPV.look;
      fpv.pitch = Math.max(-1.45, Math.min(1.45, fpv.pitch - dy * FPV.look));
      placeCamera();
    };
    const typing = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      return Boolean(el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable));
    };
    const MOVE_KEYS = new Set(["KeyW", "KeyA", "KeyS", "KeyD", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight",
      "ShiftLeft", "ShiftRight", "Space", "KeyC"]);
    const onKeyDown = (event: KeyboardEvent) => {
      if (!fpv.active || typing(event.target)) return;
      if (event.code === "Escape") {
        ctx.exitFpv();
        return;
      }
      if (event.code === "KeyE") {
        selectAtCrosshair();
        return;
      }
      if (event.code === "KeyH" && !event.repeat) {
        fpv.forklift = !fpv.forklift;
        fpv.eye = fpv.forklift ? FPV.eyeForklift : FPV.eyePerson;
        placeCamera();
        notifyFpv();
        return;
      }
      if (MOVE_KEYS.has(event.code)) {
        fpv.keys.add(event.code);
        event.preventDefault();
      }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      fpv.keys.delete(event.code);
    };
    const onBlur = () => fpv.keys.clear();
    const onLockChange = () => {
      const locked = document.pointerLockElement === renderer.domElement;
      const wasLocked = fpv.locked;
      fpv.locked = locked;
      // Brauzer Esc ni o'zi ushlaydi va faqat «qulf bo'shadi» deydi —
      // shuning uchun qulfning yo'qolishi FPV dan chiqish hisoblanadi.
      if (wasLocked && !locked && fpv.active) ctx.exitFpv();
      else notifyFpv();
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    document.addEventListener("pointerlockchange", onLockChange);

    /** Bir kadr yurish: tezlik silliq, kadr chastotasidan mustaqil. */
    const stepFpv = (now: number) => {
      const dt = Math.min(0.1, Math.max(0, (now - fpv.last) / 1000));
      fpv.last = now;
      const k = fpv.keys;
      const forward = (k.has("KeyW") || k.has("ArrowUp") ? 1 : 0) - (k.has("KeyS") || k.has("ArrowDown") ? 1 : 0);
      const strafe = (k.has("KeyD") || k.has("ArrowRight") ? 1 : 0) - (k.has("KeyA") || k.has("ArrowLeft") ? 1 : 0);
      const speed = (k.has("ShiftLeft") || k.has("ShiftRight") ? FPV.run : FPV.walk) * 1000;
      let tx = 0;
      let tz = 0;
      if (forward || strafe) {
        const length = Math.hypot(forward, strafe);
        const sin = Math.sin(fpv.yaw);
        const cos = Math.cos(fpv.yaw);
        // Kamera yaw=0 da -Z ga qaraydi.
        tx = ((-sin * forward + cos * strafe) / length) * speed;
        tz = ((-cos * forward - sin * strafe) / length) * speed;
      }
      const blend = 1 - Math.exp(-dt * 12);
      fpv.vx += (tx - fpv.vx) * blend;
      fpv.vz += (tz - fpv.vz) * blend;
      let changed = false;
      if (Math.abs(fpv.vx) > 1 || Math.abs(fpv.vz) > 1) {
        const nx = fpv.x + fpv.vx * dt;
        const nz = fpv.z + fpv.vz * dt;
        // Devor/stellajga tegsa — bo'ylab sirpanadi.
        if (free(nx, nz)) {
          fpv.x = nx;
          fpv.z = nz;
        } else if (free(nx, fpv.z)) {
          fpv.x = nx;
          fpv.vz = 0;
        } else if (free(fpv.x, nz)) {
          fpv.z = nz;
          fpv.vx = 0;
        } else {
          fpv.vx = 0;
          fpv.vz = 0;
        }
        changed = true;
      }
      const climb = (k.has("Space") ? 1 : 0) - (k.has("KeyC") ? 1 : 0);
      if (climb) {
        fpv.eye = Math.max(0.6, Math.min(9, fpv.eye + climb * FPV.climb * dt));
        changed = true;
      }
      if (changed) placeCamera();
      // Nishon ostidagi narsa — markaz yonida yozuv.
      if (now - fpv.hoverAt > 120) {
        fpv.hoverAt = now;
        const tip = tooltip.current;
        const result = hit(null);
        if (tip) {
          if (result) {
            tip.textContent = result.text;
            tip.style.display = "block";
            tip.style.transform = `translate(${element.clientWidth / 2 + 16}px, ${element.clientHeight / 2 + 14}px)`;
          } else {
            tip.style.display = "none";
          }
        }
      }
    };

    const onDown = (event: PointerEvent) => {
      down = { x: event.clientX, y: event.clientY };
      if (fpv.active) fpv.dragging = true;
    };
    const onUp = (event: PointerEvent) => {
      const click = down && Math.hypot(event.clientX - down.x, event.clientY - down.y) <= 5;
      down = null;
      if (fpv.active) {
        fpv.dragging = false;
        if (!click) return;
        if (fpv.locked) {
          selectAtCrosshair();
          return;
        }
        // Birinchi bosish — sichqonchani ushlash. Qulf mumkin bo'lmasa
        // (sensorli yoki cheklangan brauzer) sudrab qarash ishlaydi.
        try {
          const request = renderer.domElement.requestPointerLock() as unknown as Promise<void> | undefined;
          request?.catch?.(() => {});
        } catch {
          /* qulfsiz — sudrab qarash */
        }
        return;
      }
      if (!click) return;
      onSelectRef.current(hit(event)?.selection ?? null);
    };
    let hoverAt = 0;
    const onMove = (event: PointerEvent) => {
      if (fpv.active) {
        if (fpv.locked || (fpv.dragging && event.buttons !== 0)) look(event.movementX, event.movementY);
        return;
      }
      const now = performance.now();
      if (now - hoverAt < 60 || event.buttons !== 0) return;
      hoverAt = now;
      const result = hit(event);
      const tip = tooltip.current;
      if (!tip) return;
      if (!result) {
        tip.style.display = "none";
        renderer.domElement.style.cursor = "";
        return;
      }
      const rect = renderer.domElement.getBoundingClientRect();
      tip.textContent = result.text;
      tip.style.display = "block";
      tip.style.transform = `translate(${event.clientX - rect.left + 14}px, ${event.clientY - rect.top + 12}px)`;
      renderer.domElement.style.cursor = "pointer";
    };
    renderer.domElement.addEventListener("pointerdown", onDown);
    renderer.domElement.addEventListener("pointerup", onUp);
    renderer.domElement.addEventListener("pointermove", onMove);

    // --- Chizish sikli: faqat o'zgarganda ---
    const projected = new THREE.Vector3();
    let frame = 0;
    const loop = () => {
      frame = requestAnimationFrame(loop);
      if (ctx.fly) {
        const t = Math.min(1, (performance.now() - ctx.fly.start) / 650);
        const ease = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
        camera.position.lerpVectors(ctx.fly.from, ctx.fly.to, ease);
        controls.target.lerpVectors(ctx.fly.fromTarget, ctx.fly.toTarget, ease);
        if (t >= 1) ctx.fly = null;
        ctx.dirty = true;
      }
      if (fpv.active) stepFpv(performance.now());
      const moved = fpv.active ? false : controls.update();
      if (!moved && !ctx.dirty) return;
      ctx.dirty = false;
      renderer.render(scene, camera);
      const labelsEl = labelHost.current;
      if (!labelsEl) return;
      const width = element.clientWidth;
      const height = element.clientHeight;
      for (const entries of ctx.labels.values()) {
        for (const entry of entries) {
          const distance = camera.position.distanceTo(entry.world);
          projected.copy(entry.world).project(camera);
          const visible = projected.z < 1 && distance <= entry.maxDistance &&
            Math.abs(projected.x) < 1.05 && Math.abs(projected.y) < 1.05;
          if (!visible) {
            entry.el.style.display = "none";
            continue;
          }
          entry.el.style.display = "";
          const x = (projected.x * 0.5 + 0.5) * width;
          const y = (-projected.y * 0.5 + 0.5) * height;
          entry.el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
        }
      }
    };
    loop();

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("pointerlockchange", onLockChange);
      if (document.pointerLockElement === renderer.domElement) document.exitPointerLock();
      renderer.domElement.removeEventListener("pointerdown", onDown);
      renderer.domElement.removeEventListener("pointerup", onUp);
      renderer.domElement.removeEventListener("pointermove", onMove);
      controls.dispose();
      scene.traverse((object) => {
        const mesh = object as THREE.Mesh;
        mesh.geometry?.dispose?.();
        const material = mesh.material as THREE.Material | THREE.Material[] | undefined;
        if (Array.isArray(material)) material.forEach((m) => m.dispose());
        else material?.dispose?.();
      });
      renderer.dispose();
      renderer.domElement.remove();
      labelHost.current?.replaceChildren();
      ctxRef.current = null;
    };
  }, []);

  // --- Statik qism: zal, hududlar, stellaj ramasi, o'tishlar ---
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    clearGroup(ctx.staticGroup);
    setLabels(ctx, labelHost.current, "static", []);
    const labels: LabelEntry[] = [];
    const cfg = layout.config;
    const hall = cfg.hall;

    // Hovli (binolar orasi) — to'qroq pol; ustida zal va binolar.
    if (cfg.site && cfg.site.w > 0) {
      const yard = new THREE.Mesh(
        new THREE.PlaneGeometry(cfg.site.w * S, cfg.site.d * S),
        new THREE.MeshStandardMaterial({ color: 0x0d0f11, roughness: 1 }),
      );
      yard.rotation.x = -Math.PI / 2;
      yard.position.set((cfg.site.x + cfg.site.w / 2) * S, -0.01, (cfg.site.z + cfg.site.d / 2) * S);
      ctx.staticGroup.add(yard);
    }

    // Pol va zal chegarasi.
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(hall.w * S, hall.d * S),
      new THREE.MeshStandardMaterial({ color: COLORS.floor, roughness: 1 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.set((hall.x + hall.w / 2) * S, 0, (hall.z + hall.d / 2) * S);
    ctx.staticGroup.add(floor);
    ctx.staticGroup.add(gridLines(hall, 5000));
    labels.push(makeLabel("m3d-label m3d-label-hall", "A OMBOR", v3(hall.x + 2500, 9500, hall.z - 1500), 600));

    // Hududlar.
    for (const zone of cfg.zones) {
      const tone = ZONE_TONE[zone.tone] ?? COLORS.frame;
      const fill = new THREE.Mesh(
        new THREE.PlaneGeometry(zone.rect.w * S, zone.rect.d * S),
        new THREE.MeshBasicMaterial({ color: tone, transparent: true, opacity: zone.walkable ? 0.09 : 0.05, depthWrite: false }),
      );
      fill.rotation.x = -Math.PI / 2;
      fill.position.set((zone.rect.x + zone.rect.w / 2) * S, 0.012, (zone.rect.z + zone.rect.d / 2) * S);
      ctx.staticGroup.add(fill);
      ctx.staticGroup.add(rectOutline(zone.rect, tone, 0.85, 0.02));
      const caption = zone.assumed ? `${zone.label.toUpperCase()} *` : zone.label.toUpperCase();
      labels.push(makeLabel(
        "m3d-label m3d-label-zone",
        caption,
        v3(zone.rect.x + zone.rect.w / 2, 1800, zone.rect.z + zone.rect.d / 2),
        zone.assumed ? 95 : zone.walkable ? 260 : 420,
      ));
    }

    // Devorlar — rejadagidek, eshik joyida UZILGAN. Past (1,2 m) va
    // yarim shaffof: ichkaridagi stellajlarni to'smasin, chegara esa
    // ustidagi oq chiziq bilan o'qilsin.
    const wallTop: number[] = [];
    const wallMaterial = new THREE.MeshStandardMaterial({ color: 0x2b2f35, transparent: true, opacity: 0.75 });
    for (const wall of wallPieces(cfg)) {
      const length = Math.hypot(wall.to.x - wall.from.x, wall.to.z - wall.from.z) * S;
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(length, 1.2, 0.2), wallMaterial);
      mesh.position.set(((wall.from.x + wall.to.x) / 2) * S, 0.6, ((wall.from.z + wall.to.z) / 2) * S);
      mesh.rotation.y = -Math.atan2(wall.to.z - wall.from.z, wall.to.x - wall.from.x);
      ctx.staticGroup.add(mesh);
      wallTop.push(wall.from.x * S, 1.2, wall.from.z * S, wall.to.x * S, 1.2, wall.to.z * S);
      wallTop.push(wall.from.x * S, 0.02, wall.from.z * S, wall.to.x * S, 0.02, wall.to.z * S);
    }
    ctx.staticGroup.add(segments(wallTop, COLORS.outline, 0.9));

    // Eshiklar: polda sariq ostona, ikki chetida ustun va yozuv.
    const doorLines: number[] = [];
    for (const door of cfg.doors ?? []) {
      const x0 = door.from.x * S;
      const z0 = door.from.z * S;
      const x1 = door.to.x * S;
      const z1 = door.to.z * S;
      const horizontal = door.from.z === door.to.z;
      const dx = horizontal ? 0 : 0.35;
      const dz = horizontal ? 0.35 : 0;
      doorLines.push(x0, 0.04, z0, x1, 0.04, z1);
      doorLines.push(x0 - dx, 0.04, z0 - dz, x1 - dx, 0.04, z1 - dz, x0 + dx, 0.04, z0 + dz, x1 + dx, 0.04, z1 + dz);
      doorLines.push(x0, 0, z0, x0, 2.6, z0, x1, 0, z1, x1, 2.6, z1, x0, 2.6, z0, x1, 2.6, z1);
      labels.push(makeLabel(
        "m3d-label m3d-label-door",
        door.id,
        v3((door.from.x + door.to.x) / 2, 3200, (door.from.z + door.to.z) / 2),
        320,
      ));
    }
    ctx.staticGroup.add(segments(doorLines, COLORS.accent, 0.95));

    // Stellaj ramasi: ustunlar va traverslar (faqat bor qavatlar).
    const frame: number[] = [];
    const pass: number[] = [];
    const rack = cfg.rack;
    const levelY = (level: number) => (rack.levelBottoms[String(level)] ?? 0) * S;
    for (const row of layout.rows) {
      if (row.bays.length === 0) continue;
      const zFront = row.z0 * S;
      const zBack = row.z1 * S;
      const top = rack.height * S;
      for (const bay of row.bays) {
        const x0 = bay.x0 * S;
        const x1 = bay.x1 * S;
        frame.push(x0, 0, zFront, x0, top, zFront, x0, 0, zBack, x0, top, zBack);
        for (const level of bay.cells.keys()) {
          if (level === 1) continue;
          const y = levelY(level);
          frame.push(x0, y, zFront, x1, y, zFront, x0, y, zBack, x1, y, zBack);
        }
        if (bay.pass) {
          // O'tish stellaji: polda X belgisi (rejadagi kabi).
          const y = 0.03;
          pass.push(x0, y, zFront, x1, y, zBack, x0, y, zBack, x1, y, zFront);
          pass.push(x0, y, zFront, x1, y, zFront, x0, y, zBack, x1, y, zBack);
        }
      }
      const last = row.bays[row.bays.length - 1]!.x1 * S;
      frame.push(last, 0, zFront, last, top, zFront, last, 0, zBack, last, top, zBack);
      // Qator yozuvi — g'arbiy uchida.
      // Yozuv yo'lak tomonida: orqama-orqa qatorlar yozuvi ustma-ust tushmasin.
      const labelZ = row.face === "N" ? row.z0 - 900 : row.z1 + 900;
      labels.push(makeLabel("m3d-label m3d-label-row", row.code, v3(row.bays[0]!.x0 - 1800, 300, labelZ), 230));
    }
    ctx.staticGroup.add(segments(frame, COLORS.frame, 0.32));
    ctx.staticGroup.add(segments(pass, COLORS.accent, 0.75));

    // Nazorat (marshrut boshi).
    const depot = layout.depot();
    const depotMesh = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.45),
      new THREE.MeshBasicMaterial({ color: COLORS.white }),
    );
    depotMesh.position.set(depot.x * S, 0.6, depot.z * S);
    ctx.staticGroup.add(depotMesh);

    setLabels(ctx, labelHost.current, "static", labels);

    // Uy ko'rinishi: zal janubi-sharqidan, Nazorat old planda.
    const target = new THREE.Vector3((hall.x + hall.w * 0.5) * S, 0, (hall.z + hall.d * 0.5) * S);
    ctx.home.target.copy(target);
    ctx.home.position.copy(target).add(new THREE.Vector3(0.06, 0.78, 0.62).normalize().multiplyScalar(118));
    // FPV da kamera tegilmaydi: chiqishda saqlangan ko'rinish qaytadi.
    if (!ctx.fpv.active) {
      ctx.camera.position.copy(ctx.home.position);
      ctx.controls.target.copy(target);
    }
    ctx.dirty = true;
  }, [layout]);

  // --- Kataklar: band (yaxlit) va bo'sh (shaffof) ---
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    clearGroup(ctx.cellGroup);
    const solid = cells.filter((item) => item.placed.kind === "RACK" && item.visual !== "empty");
    const ghost = cells.filter((item) => item.placed.kind === "ZONE" || item.visual === "empty");
    ctx.solidIndex = solid;
    ctx.ghostIndex = ghost;

    const box = new THREE.BoxGeometry(1, 1, 1);
    const matrix = new THREE.Matrix4();
    const color = new THREE.Color();
    const position = new THREE.Vector3();
    const scale = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();

    const solidMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.85, metalness: 0.05 });
    ctx.solidMaterial = solidMaterial;
    applyDim(solidMaterial, dimRef.current);
    const solidMesh = new THREE.InstancedMesh(box, solidMaterial, Math.max(1, solid.length));
    solidMesh.count = solid.length;
    solid.forEach((item, index) => {
      const p = item.placed;
      // Yuk balandligi qavat balandligidan past: traverslar ko'rinib tursin.
      const loadH = p.h * (item.visual === "partial" ? 0.55 : 0.8);
      position.set(p.x * S, (p.y + 60 + loadH / 2) * S, p.z * S);
      scale.set(p.w * 0.86 * S, loadH * S, p.d * 0.9 * S);
      matrix.compose(position, quaternion, scale);
      solidMesh.setMatrixAt(index, matrix);
      solidMesh.setColorAt(index, color.setHex(VISUAL_COLOR[item.visual]));
    });
    solidMesh.userData.pick = "solid";
    ctx.cellGroup.add(solidMesh);

    const ghostMesh = new THREE.InstancedMesh(
      box,
      new THREE.MeshBasicMaterial({ color: COLORS.empty, transparent: true, opacity: 0.07, depthWrite: false }),
      Math.max(1, ghost.length),
    );
    ghostMesh.count = ghost.length;
    const slotLines: number[] = [];
    ghost.forEach((item, index) => {
      const p = item.placed;
      if (p.kind === "ZONE") {
        position.set(p.x * S, 0.03, p.z * S);
        scale.set(p.w * S, 0.04, p.d * S);
        const x0 = (p.x - p.w / 2) * S;
        const x1 = (p.x + p.w / 2) * S;
        const z0 = (p.z - p.d / 2) * S;
        const z1 = (p.z + p.d / 2) * S;
        const y = 0.025;
        slotLines.push(x0, y, z0, x1, y, z0, x1, y, z0, x1, y, z1, x1, y, z1, x0, y, z1, x0, y, z1, x0, y, z0);
      } else {
        position.set(p.x * S, (p.y + p.h / 2) * S, p.z * S);
        scale.set(p.w * 0.9 * S, p.h * 0.9 * S, p.d * 0.92 * S);
      }
      matrix.compose(position, quaternion, scale);
      ghostMesh.setMatrixAt(index, matrix);
    });
    ghostMesh.userData.pick = "ghost";
    ctx.cellGroup.add(ghostMesh);
    ctx.cellGroup.add(segments(slotLines, COLORS.frame, 0.28));
    refreshPickables(ctx);
    ctx.dirty = true;
  }, [cells]);

  // --- Buyurtma palletlari (hududlarda) ---
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    clearGroup(ctx.palletGroup);
    ctx.palletIndex = pallets;
    const labels: LabelEntry[] = [];
    if (pallets.length > 0) {
      const load = new THREE.InstancedMesh(
        new THREE.BoxGeometry(1, 1, 1),
        new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.8 }),
        pallets.length,
      );
      const base = new THREE.InstancedMesh(
        new THREE.BoxGeometry(1, 1, 1),
        new THREE.MeshStandardMaterial({ color: COLORS.palletBase, roughness: 1 }),
        pallets.length,
      );
      const matrix = new THREE.Matrix4();
      const color = new THREE.Color();
      const quaternion = new THREE.Quaternion();
      pallets.forEach((pallet, index) => {
        const p = pallet.placed;
        const w = Math.min(p.w, 1200) * S;
        const d = Math.min(p.d, 1000) * S;
        // Bir katakda bir nechta pallet — yuqoriga emas, ustma-ust
        // qo'yilmaydi: yonma-yon kichik siljish.
        const shift = (pallet.slot % 3) * 0.18;
        const x = p.x * S + shift;
        const z = p.z * S + shift;
        const loadH = 1.1;
        matrix.compose(new THREE.Vector3(x, 0.15 + loadH / 2, z), quaternion, new THREE.Vector3(w * 0.9, loadH, d * 0.9));
        load.setMatrixAt(index, matrix);
        const tone = pallet.tone === "control" ? COLORS.accent : pallet.tone === "shipping" ? COLORS.shipping : COLORS.stored;
        load.setColorAt(index, color.setHex(tone));
        matrix.compose(new THREE.Vector3(x, 0.075, z), quaternion, new THREE.Vector3(w, 0.15, d));
        base.setMatrixAt(index, matrix);
        labels.push(makeLabel("m3d-label m3d-label-pallet", pallet.label, v3(p.x, 1700, p.z), 38));
      });
      load.userData.pick = "pallet";
      base.userData.pick = "pallet";
      ctx.palletGroup.add(load, base);
    }
    setLabels(ctx, labelHost.current, "pallets", labels);
    refreshPickables(ctx);
    ctx.dirty = true;
  }, [pallets]);

  // --- Xodimlar va karalar ---
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    clearGroup(ctx.workerGroup);
    const labels: LabelEntry[] = [];
    for (const worker of workers) {
      const model = worker.kind === "forklift" ? forkliftModel(worker.stale) : personModel(worker.stale);
      model.position.set(worker.point.x * S, 0, worker.point.z * S);
      model.rotation.y = worker.heading;
      model.userData.workerId = worker.id;
      model.userData.label = `${worker.label} · ${worker.sub}`;
      ctx.workerGroup.add(model);
      const el = makeLabel(
        `m3d-tag m3d-tag-${worker.status}${worker.kind === "forklift" ? " m3d-tag-forklift" : ""}`,
        "",
        v3(worker.point.x, worker.kind === "forklift" ? 3200 : 2400, worker.point.z),
        520,
      );
      el.el.innerHTML = "";
      const square = document.createElement("span");
      square.className = "m3d-tag-sq";
      const name = document.createElement("b");
      name.textContent = worker.label;
      const sub = document.createElement("span");
      sub.textContent = worker.sub;
      el.el.append(square, name, sub);
      labels.push(el);
    }
    setLabels(ctx, labelHost.current, "workers", labels);
    refreshPickables(ctx);
    ctx.dirty = true;
  }, [workers]);

  // --- Marshrut ---
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    clearGroup(ctx.routeGroup);
    ctx.lineMaterials = [];
    const labels: LabelEntry[] = [];
    if (route && route.points.length > 1) {
      const positions: number[] = [];
      for (const point of route.points) positions.push(point.x * S, 0.12, point.z * S);
      const geometry = new LineGeometry();
      geometry.setPositions(positions);
      const material = new LineMaterial({
        color: COLORS.accent,
        linewidth: route.dashed ? 2 : 3.5,
        dashed: Boolean(route.dashed),
        dashSize: 1.2,
        gapSize: 0.8,
        transparent: true,
        opacity: 0.95,
      });
      const element = host.current;
      material.resolution.set(element?.clientWidth ?? 1, element?.clientHeight ?? 1);
      const line = new Line2(geometry, material);
      line.computeLineDistances();
      ctx.routeGroup.add(line);
      ctx.lineMaterials.push(material);
      route.stops.forEach((stop, index) => {
        const marker = new THREE.Mesh(
          new THREE.CylinderGeometry(0.32, 0.32, 0.08, 20),
          new THREE.MeshBasicMaterial({ color: index === route.stops.length - 1 ? COLORS.white : COLORS.accent }),
        );
        marker.position.set(stop.point.x * S, 0.1, stop.point.z * S);
        ctx.routeGroup.add(marker);
        labels.push(makeLabel("m3d-label m3d-label-stop", stop.label, v3(stop.point.x, 700, stop.point.z), 260));
      });
    }
    setLabels(ctx, labelHost.current, "route", labels);
    ctx.dirty = true;
  }, [route]);

  // --- Tanlov belgisi ---
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    clearGroup(ctx.selectGroup);
    const labels: LabelEntry[] = [];
    let box: { x: number; y: number; z: number; w: number; h: number; d: number; label: string } | null = null;
    if (selection?.type === "cell") {
      const item = cells.find((cell) => cell.locationId === selection.locationId);
      if (item) {
        const p = item.placed;
        box = { x: p.x, y: p.kind === "ZONE" ? 0 : p.y, z: p.z, w: p.w, h: p.kind === "ZONE" ? 1300 : p.h, d: p.d, label: item.code };
      }
    } else if (selection?.type === "pallet") {
      const pallet = pallets.find((item) => item.key === selection.key);
      if (pallet) box = { x: pallet.placed.x, y: 0, z: pallet.placed.z, w: 1250, h: 1350, d: 1050, label: pallet.label };
    } else if (selection?.type === "worker") {
      const worker = workers.find((item) => item.id === selection.id);
      if (worker) {
        const ring = new THREE.Mesh(
          new THREE.RingGeometry(1.1, 1.3, 40),
          new THREE.MeshBasicMaterial({ color: COLORS.white, side: THREE.DoubleSide, transparent: true, opacity: 0.9 }),
        );
        ring.rotation.x = -Math.PI / 2;
        ring.position.set(worker.point.x * S, 0.05, worker.point.z * S);
        ctx.selectGroup.add(ring);
      }
    }
    if (box) {
      const edges = new THREE.LineSegments(
        new THREE.EdgesGeometry(new THREE.BoxGeometry(box.w * S * 1.04, box.h * S * 1.04, box.d * S * 1.06)),
        new THREE.LineBasicMaterial({ color: COLORS.white }),
      );
      edges.position.set(box.x * S, (box.y + box.h / 2) * S, box.z * S);
      ctx.selectGroup.add(edges);
      // Uzoqdan ham ko'rinsin: tik nur.
      const top = Math.max(9, (box.y + box.h) * S + 2);
      ctx.selectGroup.add(segments([box.x * S, (box.y + box.h) * S, box.z * S, box.x * S, top, box.z * S], COLORS.white, 0.8));
      labels.push(makeLabel("m3d-label m3d-label-select", box.label, new THREE.Vector3(box.x * S, top, box.z * S), 900));
    }
    setLabels(ctx, labelHost.current, "select", labels);
    ctx.dirty = true;
  }, [selection, cells, pallets, workers]);

  useImperativeHandle(ref, () => ({
    zoomIn: () => {
      if (!ctxRef.current?.fpv.active) dolly(ctxRef.current, 0.72);
    },
    zoomOut: () => {
      if (!ctxRef.current?.fpv.active) dolly(ctxRef.current, 1.38);
    },
    reset: () => {
      const ctx = ctxRef.current;
      if (!ctx) return;
      ctx.exitFpv();
      fly(ctx, ctx.home.position.clone(), ctx.home.target.clone());
    },
    enterFpv: (spawn, yaw) => ctxRef.current?.enterFpv(spawn, yaw),
    exitFpv: () => ctxRef.current?.exitFpv(),
    focusRect: (rect) => {
      const ctx = ctxRef.current;
      if (!ctx || ctx.fpv.active) return;
      const target = new THREE.Vector3((rect.x + rect.w / 2) * S, 0, (rect.z + rect.d / 2) * S);
      const size = Math.max(rect.w, rect.d * 1.6) * S;
      const distance = Math.min(300, size * 0.8 + 18);
      const direction = new THREE.Vector3(0.12, 0.62, 0.78).normalize();
      fly(ctx, target.clone().add(direction.multiplyScalar(distance)), target);
    },
    focusPoints: (points) => {
      const ctx = ctxRef.current;
      if (!ctx || points.length === 0 || ctx.fpv.active) return;
      const xs = points.map((p) => p.x);
      const zs = points.map((p) => p.z);
      const x0 = Math.min(...xs);
      const x1 = Math.max(...xs);
      const z0 = Math.min(...zs);
      const z1 = Math.max(...zs);
      const size = Math.max((x1 - x0) * S, (z1 - z0) * S * 1.7, 24);
      // Chap karta sahnaning bir qismini yopadi: markaz biroz chapga
      // suriladi va marshrut ochiq qismda ko'rinadi (kamera janubdan).
      const target = new THREE.Vector3(((x0 + x1) / 2) * S - size * 0.14, 0, ((z0 + z1) / 2) * S);
      const direction = new THREE.Vector3(0.04, 0.9, 0.44).normalize();
      fly(ctx, target.clone().add(direction.multiplyScalar(Math.min(260, size * 1.05 + 10))), target);
    },
    focusPoint: (point) => {
      const ctx = ctxRef.current;
      if (!ctx) return;
      // FPV da — o'sha joyga «teleport», qarash yo'nalishi saqlanadi.
      if (ctx.fpv.active) {
        ctx.enterFpv(point, ctx.fpv.yaw);
        return;
      }
      const offset = ctx.camera.position.clone().sub(ctx.controls.target);
      const length = Math.min(Math.max(offset.length(), 26), 50);
      const target = new THREE.Vector3(point.x * S - length * 0.16, 0, point.z * S);
      fly(ctx, target.clone().add(offset.setLength(length)), target);
    },
  }));

  if (failed) {
    return (
      <div className="m3d-fallback">
        <b>3D ko'rinish ochilmadi</b>
        <span>Brauzer WebGL ni qo'llamaydi: {failed}</span>
      </div>
    );
  }

  return (
    <div className="m3d-canvas" ref={host}>
      <div className="m3d-labels" ref={labelHost} />
      <div className="m3d-tooltip" ref={tooltip} />
    </div>
  );
});

// ---------------------------------------------------------------------------
// Yordamchilar
// ---------------------------------------------------------------------------

function v3(xMM: number, yMM: number, zMM: number) {
  return new THREE.Vector3(xMM * S, yMM * S, zMM * S);
}

function clearGroup(group: THREE.Group) {
  for (const child of [...group.children]) {
    group.remove(child);
    child.traverse((object) => {
      const mesh = object as THREE.Mesh;
      mesh.geometry?.dispose?.();
      const material = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(material)) material.forEach((m) => m.dispose());
      else material?.dispose?.();
    });
  }
}

function refreshPickables(ctx: Ctx) {
  ctx.pickables = [...ctx.workerGroup.children, ...ctx.palletGroup.children, ...ctx.cellGroup.children.filter(
    (child) => child.userData.pick !== undefined,
  )];
}

function makeLabel(className: string, text: string, world: THREE.Vector3, maxDistance: number): LabelEntry {
  const el = document.createElement("div");
  el.className = className;
  el.textContent = text;
  el.style.display = "none";
  return { el, world, maxDistance };
}

function setLabels(ctx: Ctx, hostEl: HTMLDivElement | null, key: string, entries: LabelEntry[]) {
  for (const old of ctx.labels.get(key) ?? []) old.el.remove();
  ctx.labels.set(key, entries);
  if (hostEl) for (const entry of entries) hostEl.appendChild(entry.el);
  ctx.dirty = true;
}

function segments(points: number[], color: number, opacity: number) {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(points, 3));
  return new THREE.LineSegments(
    geometry,
    new THREE.LineBasicMaterial({ color, transparent: opacity < 1, opacity, depthWrite: opacity >= 1 }),
  );
}

function rectOutline(rect: Rect, color: number, opacity: number, y: number) {
  const x0 = rect.x * S;
  const x1 = (rect.x + rect.w) * S;
  const z0 = rect.z * S;
  const z1 = (rect.z + rect.d) * S;
  return segments([x0, y, z0, x1, y, z0, x1, y, z0, x1, y, z1, x1, y, z1, x0, y, z1, x0, y, z1, x0, y, z0], color, opacity);
}

function gridLines(rect: Rect, step: number) {
  const points: number[] = [];
  for (let x = rect.x + step; x < rect.x + rect.w; x += step) {
    points.push(x * S, 0.005, rect.z * S, x * S, 0.005, (rect.z + rect.d) * S);
  }
  for (let z = rect.z + step; z < rect.z + rect.d; z += step) {
    points.push(rect.x * S, 0.005, z * S, (rect.x + rect.w) * S, 0.005, z * S);
  }
  return segments(points, COLORS.grid, 1);
}

function dolly(ctx: Ctx | null, factor: number) {
  if (!ctx) return;
  const offset = ctx.camera.position.clone().sub(ctx.controls.target);
  const length = Math.min(ctx.controls.maxDistance, Math.max(ctx.controls.minDistance, offset.length() * factor));
  fly(ctx, ctx.controls.target.clone().add(offset.setLength(length)), ctx.controls.target.clone());
}

function fly(ctx: Ctx, position: THREE.Vector3, target: THREE.Vector3) {
  ctx.fly = {
    from: ctx.camera.position.clone(), to: position,
    fromTarget: ctx.controls.target.clone(), toTarget: target, start: performance.now(),
  };
  ctx.dirty = true;
}

function applyDim(material: THREE.MeshStandardMaterial, dim: boolean) {
  material.transparent = dim;
  material.opacity = dim ? 0.22 : 1;
  material.depthWrite = !dim;
  material.needsUpdate = true;
}

function material(color: number, stale: boolean) {
  return new THREE.MeshStandardMaterial({
    color, roughness: 0.6, metalness: 0.1, transparent: stale, opacity: stale ? 0.32 : 1,
  });
}

/** Piyoda xodim: tana va bosh, polda halqa. */
function personModel(stale: boolean) {
  const group = new THREE.Group();
  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.26, 1.15, 16), material(COLORS.white, stale));
  body.position.y = 0.6;
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.2, 16, 12), material(COLORS.white, stale));
  head.position.y = 1.42;
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(0.42, 0.52, 28),
    new THREE.MeshBasicMaterial({ color: COLORS.white, side: THREE.DoubleSide, transparent: true, opacity: stale ? 0.2 : 0.6 }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.03;
  group.add(body, head, ring);
  return group;
}

/** 2 t kara: korpus, ustun (machta), vilkalar, himoya ramasi. */
function forkliftModel(stale: boolean) {
  const group = new THREE.Group();
  const bodyMaterial = material(COLORS.accent, stale);
  const dark = material(0x2a2d32, stale);
  const body = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.9, 1.9), bodyMaterial);
  body.position.set(0, 0.75, -0.25);
  const counterweight = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.6, 0.45), dark);
  counterweight.position.set(0, 0.9, -1.25);
  const guardPosts = [-0.48, 0.48].flatMap((x) => [0.45, -0.95].map((z) => {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.06, 1.0, 0.06), dark);
    post.position.set(x, 1.7, z);
    return post;
  }));
  const roof = new THREE.Mesh(new THREE.BoxGeometry(1.05, 0.05, 1.5), dark);
  roof.position.set(0, 2.2, -0.25);
  const masts = [-0.38, 0.38].map((x) => {
    const mast = new THREE.Mesh(new THREE.BoxGeometry(0.09, 2.3, 0.09), dark);
    mast.position.set(x, 1.15, 0.8);
    return mast;
  });
  const forks = [-0.3, 0.3].map((x) => {
    const fork = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.05, 1.1), material(0x8a9099, stale));
    fork.position.set(x, 0.12, 1.4);
    return fork;
  });
  const wheels = [-0.55, 0.55].flatMap((x) => [0.45, -0.95].map((z) => {
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.25, 0.18, 14), dark);
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(x, 0.25, z);
    return wheel;
  }));
  group.add(body, counterweight, roof, ...guardPosts, ...masts, ...forks, ...wheels);
  return group;
}
